import { explorerTranslationDefaults, type ExplorerTranslationKey } from '../translations'

/**
 * Compare Homes chrome strings (#1713, translated in #1816).
 *
 * The operator-editable label is `compare.buttonText` (the toggle, the pick
 * chip and the Compare button); everything else the tray and the dialog print
 * comes from the explorer translations table (`compare_*` keys, plus the
 * shared night, tax and studio strings), so the shipped locale tables and the
 * operator's own Translations rows apply here like on the grid.
 *
 * `compareLabels(_t)` builds the set for one resolved translator; `COMPARE_LABELS`
 * is the English set (the schema defaults) for callers with no widget context,
 * such as the pure model and its tests.
 */
export interface CompareLabels {
  title: string
  clear: string
  close: string
  added: string
  differences: string
  copyLink: string
  linkCopied: string
  share: string
  allSame: string
  included: string
  notListed: string
  price: string
  priceDates: string
  sleeps: string
  bedrooms: string
  bathrooms: string
  location: string
  amenities: string
  view: string
  oneMore: string
  studio: string
  nightOne: string
  nightOther: string
  inclTaxes: string
  exclTaxes: string
  exclTaxesFees: string
  /** "From $280 / night" */
  priceFrom: (amount: string) => string
  /** "Tap "+ Compare" on up to 3 homes" */
  modeHint: (button: string, max: number) => string
  ofMax: (count: number, max: number) => string
  maxReached: (max: number) => string
  removeHome: (title: string) => string
  pickAria: (button: string, title: string) => string
  copyFailed: (url: string) => string
  /** One or more linked homes the page could not load. */
  missing: (count: number) => string
}

export type CompareTranslator = (key: ExplorerTranslationKey) => string

/** `%1`, `%2`… filled in order, the way the explorer's other `%n` strings are. */
function fill(template: string, ...args: Array<string | number>): string {
  return template.replace(/%(\d)/g, (m, n: string) => {
    const value = args[Number(n) - 1]
    return value === undefined ? m : String(value)
  })
}

export function compareLabels(_t: CompareTranslator): CompareLabels {
  return {
    title: _t('compare_title'),
    clear: _t('compare_clear'),
    close: _t('compare_close'),
    added: _t('compare_added'),
    differences: _t('compare_differences'),
    copyLink: _t('compare_copy_link'),
    linkCopied: _t('compare_link_copied'),
    share: _t('compare_share'),
    allSame: _t('compare_all_same'),
    included: _t('compare_included'),
    notListed: _t('compare_not_listed'),
    price: _t('compare_price'),
    priceDates: _t('compare_price_dates'),
    sleeps: _t('compare_sleeps'),
    bedrooms: _t('compare_bedrooms'),
    bathrooms: _t('compare_bathrooms'),
    location: _t('compare_location'),
    amenities: _t('compare_amenities'),
    view: _t('compare_view'),
    oneMore: _t('compare_one_more'),
    studio: _t('feature_studio'),
    nightOne: _t('price_night_one'),
    nightOther: _t('price_night_other'),
    inclTaxes: _t('price_incl_taxes'),
    exclTaxes: _t('price_excl_taxes'),
    exclTaxesFees: _t('price_excl_taxes_fees'),
    priceFrom: (amount) => fill(_t('compare_price_from'), amount),
    modeHint: (button, max) => fill(_t('compare_mode_hint'), button, max),
    ofMax: (count, max) => fill(_t('compare_of_max'), count, max),
    maxReached: (max) => fill(_t('compare_max_reached'), max),
    removeHome: (title) => fill(_t('compare_remove_home'), title),
    // The chip's accessible name is what it prints plus the home: no words of its own to translate.
    pickAria: (button, title) => `${button} ${title}`,
    copyFailed: (url) => fill(_t('compare_copy_failed'), url),
    missing: (count) => (count === 1 ? _t('compare_missing_one') : fill(_t('compare_missing_other'), count)),
  }
}

const english: CompareTranslator = (key) => explorerTranslationDefaults[key].en_US

/** The English set: the schema defaults, for code with no widget context. */
export const COMPARE_LABELS: CompareLabels = compareLabels(english)
